<script lang="ts" setup>
import type { ComponentPublicInstance } from 'vue';
import type { SparkleId } from './config';

import { computed, nextTick, onBeforeUnmount, reactive, ref, watch } from 'vue';
import { sparkles } from './config';
import Glyph from './Glyph.vue';

const { animate = false } = defineProps<{ animate?: boolean }>();

type GlyphInstance = InstanceType<typeof Glyph>;

const leftLoopAnchor = ref<SVGAnimationElement | null>(null);
const glyphRefs = reactive<Record<SparkleId, GlyphInstance | null>>({
	left: null,
	right: null,
});
const animating = reactive<Record<SparkleId, boolean>>({
	left: false,
	right: false,
});

let stopAfterCurrentCycle = false;
let animationStartVersion = 0;
let removeLoopBoundaryListener: (() => void) | null = null;

const renderedSparkles = computed(() =>
	sparkles.map((sparkle) => ({
		sparkle,
		animating: animating[sparkle.id],
		onLoopAnchorChange: sparkle.id === 'left' ? onLeftLoopAnchorChange : null,
	})),
);

async function startAnimation() {
	stopAfterCurrentCycle = false;

	if (animating.left && animating.right) return;

	animating.left = true;
	animating.right = true;

	const currentStartVersion = ++animationStartVersion;

	await nextTick();

	if (currentStartVersion !== animationStartVersion || animate === false) return;

	glyphRefs.left?.beginAnimations();
	glyphRefs.right?.beginAnimations();
}

function stopAnimation() {
	if (animating.left === false && animating.right === false) return;

	animationStartVersion++;
	animating.left = false;
	animating.right = false;
	stopAfterCurrentCycle = false;
}

function onLoopBoundary() {
	if (animate === false && stopAfterCurrentCycle) {
		stopAnimation();
	}
}

function repeatListener() {
	onLoopBoundary();
}

function onLeftLoopAnchorChange(element: SVGAnimationElement | null) {
	leftLoopAnchor.value = element;
}

function onGlyphRefChange(
	id: SparkleId,
	element: Element | ComponentPublicInstance | GlyphInstance | null,
) {
	glyphRefs[id] = element && 'beginAnimations' in element ? (element as GlyphInstance) : null;
}

function syncLoopBoundaryListener(element: SVGAnimationElement | null) {
	removeLoopBoundaryListener?.();
	removeLoopBoundaryListener = null;

	if (element === null) return;

	element.addEventListener('repeatEvent', repeatListener as EventListener);

	removeLoopBoundaryListener = () => {
		element.removeEventListener('repeatEvent', repeatListener as EventListener);
	};
}

async function onAnimateChange(animate: boolean) {
	if (animate) {
		await startAnimation();
		return;
	}

	if (animating.left || animating.right) {
		stopAfterCurrentCycle = true;
	}
}

watch(() => animate, (animate) => onAnimateChange(animate), { immediate: true });
watch(leftLoopAnchor, (element) => syncLoopBoundaryListener(element), { flush: 'post' });

onBeforeUnmount(() => {
	removeLoopBoundaryListener?.();
});
</script>

<template>
	<svg
		class="overflow-visible"
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
	>
		<Glyph
			v-for="item in renderedSparkles"
			:key="item.sparkle.id"
			:ref="(element) => onGlyphRefChange(item.sparkle.id, element)"
			:sparkle="item.sparkle"
			:animating="item.animating"
			:on-loop-anchor-change="item.onLoopAnchorChange"
		/>
	</svg>
</template>
